import React, { ReactElement, useEffect, useState } from 'react';
import './predpisyCudziJazykTableComponent.scss';
import {
  Loader, Table, Tbody, Td, Th, Thead, Tr, Text,
} from 'idsk-react-components';
import { Message, MobilePerPageSelect, Pagination } from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import { perPageDataArray } from '../../../constants/PaginationPerPageConstants';
import usePredpisyCudziJazykTableComponent from '../hook/usePredpisyCudziJazykTableComponent';
import { getLastPage, getPaginationTotalLabel } from '../../../function/getPaginationTotalLabel';
import PredpisCudziJazyk from '../../../model/PredpisCudziJazyk';
import DownloadButton from './DownloadButton';
import PredpisCudziJazykMobileTableItem from './PredpisCudziJazykMobileTableItem';

interface Props {
  id: string | undefined,
  totalElements: number,
  predpisyCudziJazyk: PredpisCudziJazyk | undefined | null,
}

const PredpisyCudziJazykTableComponent = ({
  id,
  totalElements,
  predpisyCudziJazyk,
}: Props): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayError, setDisplayError] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const {
    pageNumberFromUrl,
    perPageValue,
    data,
    status,
  } = usePredpisyCudziJazykTableComponent(id, totalElements, getLastPage);

  if (totalElements === 0) {
    return <p className="text-center">Nenašli sa žiadne záznamy.</p>;
  }

  if (!data) {
    return <></>;
  }

  const renderPredpisy = (predpisy): ReactElement[] => {
    const predpisyList: ReactElement[] = [];
    predpisy.results.forEach((item) => {
      predpisyList.push(
        <Tr key={`predpisyCudziJazykTableComponentKey-${item['@id']}`}>
          <Td tdStyle="numeric">{item.predpisSk}</Td>
          <Td tdStyle="numeric">{item.predpisCudzi || item.predpisSk}</Td>
          <Td tdStyle="numeric">
            <DownloadButton
              url={process.env.REACT_APP_MAGNOLIA_API_BASE_PATH + item.pdf}
            />
          </Td>
        </Tr>,
      );
    });
    return predpisyList;
  };

  const renderPredpisyMobile = (predpisy, nadpisy): ReactElement[] => {
    const predpisyList: ReactElement[] = [];
    predpisy.results.forEach((item) => {
      predpisyList.push(
        <PredpisCudziJazykMobileTableItem
          key={`predpisyCudziJazykTableComponentKey-${item['@id']}`}
          predpisSk={item.predpisSk}
          predpisCudzi={item.predpisCudzi || item.predpisSk}
          predpisyCudziJazyk={nadpisy}
          actions={(
            <DownloadButton
              url={process.env.REACT_APP_MAGNOLIA_API_BASE_PATH + item.pdf}
            />
              )}
        />,
      );
    });
    return predpisyList;
  };

  const getLabel = (predpisy: PredpisCudziJazyk | undefined | null): ReactElement => {
    const isTranslation = predpisy?.nadpisStlpecPrvyCudzi && predpisy?.nadpisStlpecDruhyCudzi;
    return (
      <Text className={`pagination-label ${isTranslation ? '' : 'table-label-margin'}`}>
        <span className="items-total-info">
          {getPaginationTotalLabel(perPageValue, totalElements, pageNumberFromUrl)}
        </span>
      </Text>
    );
  };

  return (
    <div className="predpisy-cudzi-jazyk-table-component">
      <Loader loading={status.loading}>
        {(status.responseCode && status.responseCode > 399) ? (
          <>
            {displayError && (
            <Message
              type="error"
              onClear={() => setDisplayError(false)}
              headingSize="m"
            >
              Nepodarilo sa načítať právne predpisy.
            </Message>
            )}
          </>
        ) : (
          <>
            {isMobile ? (
              <>
                <MobilePerPageSelect
                  onRecordsPerPageChange={(value) => {
                    const updatedSearchParams = new URLSearchParams(searchParams.toString());
                    updatedSearchParams.set('perPage', value.toString());
                    setSearchParams(updatedSearchParams.toString());
                  }}
                  recordsPerPage={perPageDataArray}
                  perPageValue={perPageValue}
                  totalText={getPaginationTotalLabel(perPageValue, totalElements, pageNumberFromUrl)}
                />
                <div className="predpisy-cudzi-jazyk-table-mobile">
                  {renderPredpisyMobile(data, predpisyCudziJazyk)}
                </div>
              </>
            ) : (
              <>
                {getLabel(predpisyCudziJazyk)}
                <Table className="predpisy-cudzi-jazyk-table">
                  <Thead>
                    <Tr>
                      <Th thStyle="numeric" scope="row">
                        {predpisyCudziJazyk?.nadpisStlpecPrvySk}
                        {predpisyCudziJazyk?.nadpisStlpecPrvyCudzi && <br />}
                        {predpisyCudziJazyk?.nadpisStlpecPrvyCudzi}
                      </Th>
                      <Th thStyle="numeric" scope="row">
                        {predpisyCudziJazyk?.nadpisStlpecDruhySk}
                        {predpisyCudziJazyk?.nadpisStlpecDruhyCudzi && <br />}
                        {predpisyCudziJazyk?.nadpisStlpecDruhyCudzi}
                      </Th>
                      {/* add border */}
                      <Th thStyle="numeric" scope="row" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {renderPredpisy(data)}
                  </Tbody>
                </Table>
              </>
            )}
            <Pagination
              hideTotal
              pageQueryName="page"
              totalElements={totalElements}
              size={perPageValue}
              number={pageNumberFromUrl}
              onRecordsPerPageChange={(value) => {
                const updatedSearchParams = new URLSearchParams(searchParams.toString());
                updatedSearchParams.set('perPage', value.toString());
                setSearchParams(updatedSearchParams.toString());
              }}
              recordsPerPage={perPageDataArray}
              perPageValue={perPageValue}
            />
          </>
        )}
      </Loader>
    </div>
  );
};

export default PredpisyCudziJazykTableComponent;
